import { endOfDay, parseISO, startOfDay } from 'date-fns';
import type { BookingSessionNoteSummary, SessionNoteStatus } from '@/types/sessions';

/** What the "Session note" cell of a booking row shows. */
export type NoteCellState =
  | { kind: 'open'; noteId: string; status: SessionNoteStatus } // the viewer's own note
  | { kind: 'locked'; status: SessionNoteStatus } // someone else's note: status only
  | { kind: 'write'; missing: boolean } // no note yet and the viewer may write it
  | { kind: 'later' } // the session has not happened yet
  | { kind: 'none' };

const NO_SESSION = new Set(['cancelled', 'no_show']);

/**
 * Pure so it can be tested. A note can be written from the day of the session
 * on; a past session without one is "missing". Bookings that did not take
 * place get nothing, unless a note was written anyway.
 */
export function noteCellState(
  summary: BookingSessionNoteSummary | null | undefined,
  startTime: string | null | undefined,
  bookingStatus: string,
  now: Date = new Date(),
): NoteCellState {
  if (!summary) return { kind: 'none' };
  if (summary.status) {
    return summary.mine && summary.id
      ? { kind: 'open', noteId: summary.id, status: summary.status }
      : { kind: 'locked', status: summary.status };
  }
  if (NO_SESSION.has(bookingStatus) || !summary.canWrite || !startTime) return { kind: 'none' };

  const start = parseISO(startTime);
  if (start > endOfDay(now)) return { kind: 'later' };
  return { kind: 'write', missing: start < startOfDay(now) };
}
